import type { Ready } from "@beacon/core/change-requests/types"
import type { NoticeReason } from "@beacon/core/projects/types"
import type { InboxNotice } from "./index"

/*
 * What the Beacon inbox's notices say: one wording for an inbox row, a push's preview and a Slack
 * DM, so a notice reads the same wherever it is told. Each caller names the work its own way (a
 * key, a Slack link). Pure: the web app imports it.
 */

/**
 * What a work notice says its actor did, after their name: every reason its Member has to hear of
 * it, on `ticket` as the caller names it (a key, a Slack link, "a ticket").
 */
export function workNoticeWords(
  { reasons, comment }: { reasons: readonly NoticeReason[]; comment: boolean },
  ticket: string,
) {
  const assigned = reasons.includes("assigned")
  const mentioned = reasons.includes("mentioned")
  const commented = reasons.includes("commented-on-your-ticket")
  const done: string[] = []
  if (assigned) done.push(`assigned you to ${ticket}`)
  if (comment && mentioned) done.push(`mentioned you in a comment on ${assigned ? "it" : ticket}`)
  else if (comment && commented) done.push(`commented on ${assigned ? "it" : ticket}`)
  else if (mentioned) done.push(assigned ? "mentioned you in it" : `mentioned you in ${ticket}`)
  const yours = commented ? ", which you're assigned to" : ""
  return `${done.join(" and ")}${yours}`
}

/** What a change request has ready for its driver, after its name. */
const readyWords: Record<Ready, string> = {
  round: "has new Questions for you",
  concluded: "has no more Questions: your Answers are ready to review",
  spec: "has its Spec written for you to shape",
  ticket: "has its Suggested ticket written for you to shape",
  plan: "has its Ticket plan ready for you to review",
  reply: "has a reply from the agent",
  failed: "stopped: the agent's turn failed. Try it again from its page",
  "stopped-at-ceiling":
    "stopped at its cost ceiling. An Admin can raise it, then you can try again",
  "stopped-at-step-cap": "stopped at its step cap. Try it again from its page",
  finished: "has finished the agent's turn",
  waiting: "is waiting for your approval of a read. Review it from its page",
}

/**
 * What a change request's notice says, as a sentence without its full stop: what its agent has
 * ready for its driver, or that `actor` (as the caller names them) took theirs over; the change
 * request as the caller names it (`changeRequest`).
 */
export const changeRequestWords = (
  notice: { event: "ready"; ready: Ready } | { event: "taken-over" },
  changeRequest: string,
  actor: string,
) =>
  notice.event === "ready"
    ? `Your change request ${changeRequest} ${readyWords[notice.ready]}`
    : `${actor} took over your change request ${changeRequest}`

/** A change request as an inbox row and a push name it: by its Project's key and its number. */
export const changeRequestName = (projectKey: string, number: number) =>
  `${projectKey} change request ${number}`

/** What a Member merge's notice says first. */
export const accountsMergedWords = "An Admin merged two of your Beacon accounts into this one"

/** Names in a sentence: "A", "A and B", "A, B and C". */
const listed = (names: readonly string[]) =>
  names.length > 1 ? `${names.slice(0, -1).join(", ")} and ${names.at(-1)}` : (names[0] ?? "")

/**
 * What a Member merge's notice says after `accountsMergedWords`, as sentences: which Connectors'
 * accounts moved to the kept Member paused, and where resuming them is (`connectors`, as the
 * caller names My Connectors), then where their own was kept and the other's replaced. Catalog
 * names alone.
 */
export function connectorMergeWords(
  { moved, replaced }: { moved: readonly string[]; replaced: readonly string[] },
  connectors: string,
) {
  const said: string[] = []
  if (moved.length) {
    const one = moved.length === 1
    said.push(
      `Your ${listed(moved)} ${one ? "account" : "accounts"} moved here paused: Beacon's agent won't use ${one ? "it" : "them"} until you resume ${one ? "it" : "them"} in ${connectors}.`,
    )
  }
  if (replaced.length)
    said.push(
      `You had ${listed(replaced)} connected on both, so Beacon kept this one's and stopped using the other's; ${connectors} says what became of its access.`,
    )
  return said
}

/**
 * What an inbox entry's notice says, as its reader may see it now: its `line` (who did what, on
 * what) and `about` (the work's title, or what a Member merge did), or, for work they can no
 * longer read, only what kind of thing it was about.
 */
export function inboxWords(notice: InboxNotice): { line: string; about: string | null } {
  switch (notice.kind) {
    case "ticket":
      return {
        line: `${notice.actor?.name ?? "Someone"} ${workNoticeWords(
          { reasons: notice.reasons, comment: notice.inComment },
          notice.ticket.key,
        )}`,
        about: notice.ticket.title,
      }
    case "change-request": {
      const { project, number, title } = notice.changeRequest
      return {
        line: changeRequestWords(
          notice,
          changeRequestName(project.key, number),
          (notice.event === "taken-over" && notice.actor?.name) || "Someone",
        ),
        about: title,
      }
    }
    case "account":
      return {
        line: accountsMergedWords,
        about: connectorMergeWords(notice, "My Connectors").join(" ") || null,
      }
    case "unavailable":
      return {
        line: `A notice about ${notice.was === "ticket" ? "a ticket" : "a change request"} you can no longer open`,
        about: null,
      }
  }
}
